import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageContent,
} from "beproduct-ui";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/navigation/page-header";

interface LicenseStatus {
  state: "active" | "expired" | "missing" | "invalid_signature";
  domain: string | null;
  validUntil: string | null;
  issuedAt: string | null;
  lastCheckedAt: string | null;
  lastCheckSucceeded: boolean;
  lastCheckError: string | null;
}

export const Route = createFileRoute("/_authed/$companySlug/license")({
  component: LicensePage,
});

function LicensePage() {
  const { company, isAdmin } = Route.useRouteContext();
  const nav = useNavigate({ from: "/$companySlug/license" });
  const qc = useQueryClient();
  const [jwt, setJwt] = useState("");
  const [importError, setImportError] = useState<string | null>(null);

  const statusQ = useQuery({
    queryKey: ["license", company.id],
    queryFn: () => api<LicenseStatus>(`/api/tenants/${company.id}/license`),
  });

  const recheck = useMutation({
    mutationFn: () =>
      api<LicenseStatus>(
        `/api/tenants/${company.id}/license/recheck`,
        { method: "POST" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["license", company.id] }),
  });

  const importMut = useMutation({
    mutationFn: (token: string) =>
      api<LicenseStatus>(`/api/tenants/${company.id}/license/import`, {
        method: "POST",
        body: JSON.stringify({ jwt: token }),
      }),
    onSuccess: () => {
      setJwt("");
      setImportError(null);
      qc.invalidateQueries({ queryKey: ["license", company.id] });
      qc.invalidateQueries({ queryKey: ["auth", "companies"] });
      nav({ to: "/$companySlug", params: { companySlug: company.slug } });
    },
    onError: (err) => {
      setImportError(err instanceof Error ? err.message : String(err));
    },
  });

  const s = statusQ.data;

  return (
    <PageContent>
      <div className="mx-auto max-w-2xl pt-4 pb-8 md:pt-6 space-y-6">
        <PageHeader title="License" status={<span className="text-sm text-warning">License inactive</span>} />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-warning">
              <AlertTriangle className="size-5" /> License inactive
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              <strong>{company.name}</strong> ({s?.domain ?? "—"}) does not have
              a valid license. Syncing, reports, and tenant management are
              blocked until a valid license is restored.
            </p>
            <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs">
              <dt className="text-muted-foreground">State:</dt>
              <dd className="col-span-2 font-mono">{s?.state ?? "loading"}</dd>
              <dt className="text-muted-foreground">Valid until:</dt>
              <dd className="col-span-2 font-mono">{s?.validUntil ?? "—"}</dd>
              <dt className="text-muted-foreground">Last checked:</dt>
              <dd className="col-span-2 font-mono">
                {s?.lastCheckedAt ?? "never"}
              </dd>
              <dt className="text-muted-foreground">Last error:</dt>
              <dd className="col-span-2 font-mono text-destructive">
                {s?.lastCheckError ?? "—"}
              </dd>
            </dl>
            {isAdmin && (
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => recheck.mutate()}
                  disabled={recheck.isPending}
                >
                  <RefreshCw
                    className={
                      "size-4 " + (recheck.isPending ? "animate-spin" : "")
                    }
                  />
                  {recheck.isPending
                    ? "Checking…"
                    : "Refresh from licensing server"}
                </Button>
                <div className="space-y-2 border-t pt-3">
                  <label
                    className="text-xs text-muted-foreground"
                    htmlFor="jwt"
                  >
                    Paste license JWT (for air-gapped installs)
                  </label>
                  <textarea
                    id="jwt"
                    rows={4}
                    className="w-full rounded border bg-background p-2 font-mono text-xs"
                    value={jwt}
                    onChange={(e) => setJwt(e.target.value)}
                    placeholder="eyJhbGciOiJSUzI1NiIs…"
                  />
                  {importError && (
                    <p className="text-xs text-destructive">{importError}</p>
                  )}
                  <Button
                    size="sm"
                    onClick={() => importMut.mutate(jwt.trim())}
                    disabled={!jwt.trim() || importMut.isPending}
                  >
                    {importMut.isPending ? "Importing…" : "Import license"}
                  </Button>
                </div>
              </div>
            )}
            {!isAdmin && (
              <p className="text-xs text-muted-foreground border-t pt-3">
                Contact your administrator to restore this license.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContent>
  );
}
